/**
 * Admin Tax Settings
 * Manage tax rules, exemptions, and view tax reports
 */

import React, { useState, useEffect } from 'react';
import {
  DollarSign,
  Plus,
  Edit,
  Trash2,
  FileCheck,
  Shield,
  TrendingUp,
  Globe,
  CheckCircle,
  XCircle,
  AlertTriangle,
} from 'lucide-react';

interface TaxRule {
  id: number;
  country_code: string;
  state_code: string | null;
  region: string | null;
  tax_name: string;
  tax_type: string;
  tax_rate: number;
  applies_to: string;
  is_compound: number;
  priority: number;
  reverse_charge_enabled: number;
  tax_id_required: number;
  b2b_exempt: number;
  threshold_amount: number | null;
  threshold_type: string | null;
  is_active: number;
  effective_from: string | null;
  effective_until: string | null;
}

interface TaxExemption {
  id: number;
  client_id: number;
  exemption_type: string;
  tax_id: string | null;
  tax_id_type: string | null;
  tax_id_country: string | null;
  is_validated: number;
  validated_at: string | null;
  validated_by: number | null;
  validation_method: string | null;
  certificate_number: string | null;
  exempt_countries: string | null;
  exempt_tax_types: string | null;
  is_active: number;
  expires_at: string | null;
  notes: string | null;
  created_at: string;
}

interface TaxStatistics {
  total_tax_collected: number;
  total_transactions: number;
  by_country: Array<{
    country_code: string;
    tax_collected: number;
    count: number;
  }>;
  by_tax_type: Array<{
    tax_type: string;
    tax_collected: number;
    count: number;
  }>;
}

export const TaxSettings: React.FC = () => {
  const [rules, setRules] = useState<TaxRule[]>([]);
  const [exemptions, setExemptions] = useState<TaxExemption[]>([]);
  const [statistics, setStatistics] = useState<TaxStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState<'rules' | 'exemptions' | 'reports'>('rules');
  const [filterCountry, setFilterCountry] = useState('');
  const [filterType, setFilterType] = useState('');

  useEffect(() => {
    loadTaxRules();
    loadTaxExemptions();
    loadTaxStatistics();
  }, []);

  const loadTaxRules = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterCountry) params.append('country_code', filterCountry);
      if (filterType) params.append('tax_type', filterType);

      const response = await fetch(
        `/api/v1/admin/tax/rules?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      const data = await response.json();
      if (data.success) {
        setRules(data.data.rules);
      }
    } catch (error) {
      console.error('Failed to load tax rules:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTaxExemptions = async () => {
    try {
      const response = await fetch('/api/v1/admin/tax/exemptions', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setExemptions(data.data.exemptions);
      }
    } catch (error) {
      console.error('Failed to load tax exemptions:', error);
    }
  };

  const loadTaxStatistics = async () => {
    try {
      const response = await fetch('/api/v1/admin/tax/statistics', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setStatistics(data.data.statistics);
      }
    } catch (error) {
      console.error('Failed to load tax statistics:', error);
    }
  };

  const handleToggleRule = async (ruleId: number, isActive: boolean) => {
    try {
      const response = await fetch(`/api/v1/admin/tax/rules/${ruleId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ is_active: !isActive }),
      });
      const data = await response.json();
      if (data.success) {
        loadTaxRules();
      }
    } catch (error) {
      console.error('Failed to toggle tax rule:', error);
    }
  };

  const handleDeleteRule = async (ruleId: number) => {
    if (!confirm('Are you sure you want to delete this tax rule?')) return;

    try {
      const response = await fetch(`/api/v1/admin/tax/rules/${ruleId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        loadTaxRules();
      }
    } catch (error) {
      console.error('Failed to delete tax rule:', error);
    }
  };

  const handleValidateTaxId = async (exemptionId: number) => {
    try {
      const response = await fetch(
        `/api/v1/admin/tax/exemptions/${exemptionId}/validate`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      const data = await response.json();
      if (data.success) {
        alert('Tax ID validated successfully');
        loadTaxExemptions();
      }
    } catch (error) {
      console.error('Failed to validate tax ID:', error);
    }
  };

  const handleDeleteExemption = async (exemptionId: number) => {
    if (!confirm('Are you sure you want to delete this tax exemption?')) return;

    try {
      const response = await fetch(`/api/v1/admin/tax/exemptions/${exemptionId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        loadTaxExemptions();
      }
    } catch (error) {
      console.error('Failed to delete tax exemption:', error);
    }
  };

  const getTaxTypeBadge = (type: string) => {
    const colors = {
      vat: 'bg-orange-900 text-blue-300',
      sales_tax: 'bg-green-900 text-green-300',
      gst: 'bg-purple-900 text-purple-300',
      excise: 'bg-orange-900 text-orange-300',
    };
    return colors[type as keyof typeof colors] || 'bg-slate-700/50 text-slate-300';
  };

  const getExemptionTypeBadge = (type: string) => {
    const colors = {
      business: 'bg-orange-900 text-blue-300',
      reseller: 'bg-green-900 text-green-300',
      government: 'bg-purple-900 text-purple-300',
      nonprofit: 'bg-orange-900 text-orange-300',
      diplomatic: 'bg-red-900 text-red-300',
    };
    return colors[type as keyof typeof colors] || 'bg-slate-700/50 text-slate-300';
  };

  const formatCurrency = (amount: number) => {
    return `$${amount.toFixed(2)}`;
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <DollarSign className="w-6 h-6" />
              Tax Settings
            </h1>
            <p className="text-slate-400 mt-1">
              Manage tax rules, exemptions, and view tax reports
            </p>
          </div>
          <button className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 flex items-center gap-2">
            <Plus className="w-4 h-4" />
            New Tax Rule
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-blue-100 text-sm mb-1">Total Tax Collected</div>
                <div className="text-white text-2xl font-bold">
                  {formatCurrency(statistics.total_tax_collected)}
                </div>
              </div>
              <TrendingUp className="w-12 h-12 text-blue-200 opacity-50" />
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-green-100 text-sm mb-1">Total Transactions</div>
                <div className="text-white text-2xl font-bold">
                  {statistics.total_transactions.toLocaleString()}
                </div>
              </div>
              <FileCheck className="w-12 h-12 text-green-200 opacity-50" />
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-purple-100 text-sm mb-1">Tax Rules</div>
                <div className="text-white text-2xl font-bold">
                  {rules.filter((r) => r.is_active).length}
                </div>
                <div className="text-purple-100 text-xs">Active</div>
              </div>
              <Globe className="w-12 h-12 text-purple-200 opacity-50" />
            </div>
          </div>

          <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-orange-100 text-sm mb-1">Exemptions</div>
                <div className="text-white text-2xl font-bold">
                  {exemptions.filter((e) => e.is_active).length}
                </div>
                <div className="text-orange-100 text-xs">Active</div>
              </div>
              <Shield className="w-12 h-12 text-orange-200 opacity-50" />
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-4 mb-6 border-b border-slate-700/50">
        <button
          onClick={() => setSelectedTab('rules')}
          className={`px-4 py-2 font-medium transition-colors ${
            selectedTab === 'rules'
              ? 'text-orange-400 border-b-2 border-blue-400'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          Tax Rules ({rules.length})
        </button>
        <button
          onClick={() => setSelectedTab('exemptions')}
          className={`px-4 py-2 font-medium transition-colors ${
            selectedTab === 'exemptions'
              ? 'text-orange-400 border-b-2 border-blue-400'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          Exemptions ({exemptions.length})
        </button>
        <button
          onClick={() => setSelectedTab('reports')}
          className={`px-4 py-2 font-medium transition-colors ${
            selectedTab === 'reports'
              ? 'text-orange-400 border-b-2 border-blue-400'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          Reports
        </button>
      </div>

      {/* Tax Rules Tab */}
      {selectedTab === 'rules' && (
        <>
          {/* Filters */}
          <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm text-slate-400 mb-2">Country</label>
                <input
                  type="text"
                  value={filterCountry}
                  onChange={(e) => setFilterCountry(e.target.value)}
                  placeholder="US, GB, DE..."
                  className="w-full bg-slate-700/50 text-white rounded px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-2">Tax Type</label>
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="w-full bg-slate-700/50 text-white rounded px-3 py-2"
                >
                  <option value="">All Types</option>
                  <option value="vat">VAT</option>
                  <option value="sales_tax">Sales Tax</option>
                  <option value="gst">GST</option>
                  <option value="excise">Excise</option>
                </select>
              </div>
              <div className="flex items-end">
                <button
                  onClick={loadTaxRules}
                  className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600"
                >
                  Apply Filters
                </button>
              </div>
            </div>
          </div>

          {/* Rules Table */}
          <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-700/50">
                <tr>
                  <th className="text-left p-4 text-slate-300 font-medium">Region</th>
                  <th className="text-left p-4 text-slate-300 font-medium">Tax Name</th>
                  <th className="text-left p-4 text-slate-300 font-medium">Type</th>
                  <th className="text-left p-4 text-slate-300 font-medium">Rate</th>
                  <th className="text-left p-4 text-slate-300 font-medium">Applies To</th>
                  <th className="text-left p-4 text-slate-300 font-medium">Features</th>
                  <th className="text-left p-4 text-slate-300 font-medium">Status</th>
                  <th className="text-right p-4 text-slate-300 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {loading ? (
                  <tr>
                    <td colSpan={8} className="text-center py-8 text-slate-400">
                      Loading...
                    </td>
                  </tr>
                ) : rules.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-8 text-slate-400">
                      No tax rules found
                    </td>
                  </tr>
                ) : (
                  rules.map((rule) => (
                    <tr key={rule.id} className="hover:bg-slate-700/30">
                      <td className="p-4">
                        <span className="text-white font-medium">{rule.country_code}</span>
                        {rule.state_code && (
                          <span className="text-slate-400 ml-1">- {rule.state_code}</span>
                        )}
                      </td>
                      <td className="p-4">
                        <span className="text-white">{rule.tax_name}</span>
                      </td>
                      <td className="p-4">
                        <span
                          className={`px-2 py-1 rounded text-xs ${getTaxTypeBadge(
                            rule.tax_type
                          )}`}
                        >
                          {rule.tax_type.toUpperCase()}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className="text-white font-bold">{rule.tax_rate}%</span>
                      </td>
                      <td className="p-4">
                        <span className="text-slate-300 capitalize">{rule.applies_to}</span>
                      </td>
                      <td className="p-4">
                        <div className="flex gap-1">
                          {rule.reverse_charge_enabled === 1 && (
                            <span className="px-2 py-0.5 bg-orange-900 text-blue-300 rounded text-xs">
                              RC
                            </span>
                          )}
                          {rule.b2b_exempt === 1 && (
                            <span className="px-2 py-0.5 bg-green-900 text-green-300 rounded text-xs">
                              B2B
                            </span>
                          )}
                          {rule.is_compound === 1 && (
                            <span className="px-2 py-0.5 bg-purple-900 text-purple-300 rounded text-xs">
                              CMP
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        <button
                          onClick={() => handleToggleRule(rule.id, rule.is_active === 1)}
                          className={`px-2 py-1 rounded text-sm ${
                            rule.is_active
                              ? 'bg-green-900 text-green-300'
                              : 'bg-slate-700/50 text-slate-400'
                          }`}
                        >
                          {rule.is_active ? 'Active' : 'Inactive'}
                        </button>
                      </td>
                      <td className="p-4">
                        <div className="flex justify-end gap-2">
                          <button
                            className="p-2 text-orange-400 hover:bg-slate-700/50 rounded"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteRule(rule.id)}
                            className="p-2 text-red-400 hover:bg-slate-700/50 rounded"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Tax Exemptions Tab */}
      {selectedTab === 'exemptions' && (
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-700/50">
              <tr>
                <th className="text-left p-4 text-slate-300 font-medium">Client ID</th>
                <th className="text-left p-4 text-slate-300 font-medium">Type</th>
                <th className="text-left p-4 text-slate-300 font-medium">Tax ID</th>
                <th className="text-left p-4 text-slate-300 font-medium">Validation</th>
                <th className="text-left p-4 text-slate-300 font-medium">Expires</th>
                <th className="text-left p-4 text-slate-300 font-medium">Status</th>
                <th className="text-right p-4 text-slate-300 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50">
              {exemptions.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-slate-400">
                    No tax exemptions found
                  </td>
                </tr>
              ) : (
                exemptions.map((exemption) => (
                  <tr key={exemption.id} className="hover:bg-slate-700/30">
                    <td className="p-4">
                      <span className="text-white font-mono">#{exemption.client_id}</span>
                    </td>
                    <td className="p-4">
                      <span
                        className={`px-2 py-1 rounded text-xs ${getExemptionTypeBadge(
                          exemption.exemption_type
                        )}`}
                      >
                        {exemption.exemption_type}
                      </span>
                    </td>
                    <td className="p-4">
                      <div>
                        {exemption.tax_id ? (
                          <>
                            <span className="text-white font-mono">{exemption.tax_id}</span>
                            {exemption.tax_id_country && (
                              <span className="text-slate-400 ml-2 text-sm">
                                ({exemption.tax_id_country})
                              </span>
                            )}
                          </>
                        ) : (
                          <span className="text-gray-500">No Tax ID</span>
                        )}
                      </div>
                    </td>
                    <td className="p-4">
                      {exemption.is_validated ? (
                        <div className="flex items-center gap-1 text-green-400">
                          <CheckCircle className="w-4 h-4" />
                          <span className="text-sm">Validated</span>
                        </div>
                      ) : exemption.tax_id ? (
                        <button
                          onClick={() => handleValidateTaxId(exemption.id)}
                          className="flex items-center gap-1 text-yellow-400 hover:text-yellow-300"
                        >
                          <AlertTriangle className="w-4 h-4" />
                          <span className="text-sm">Validate</span>
                        </button>
                      ) : (
                        <div className="flex items-center gap-1 text-gray-500">
                          <XCircle className="w-4 h-4" />
                          <span className="text-sm">N/A</span>
                        </div>
                      )}
                    </td>
                    <td className="p-4">
                      {exemption.expires_at ? (
                        <span className="text-slate-300">
                          {new Date(exemption.expires_at).toLocaleDateString()}
                        </span>
                      ) : (
                        <span className="text-gray-500">No expiry</span>
                      )}
                    </td>
                    <td className="p-4">
                      <span
                        className={`px-2 py-1 rounded text-sm ${
                          exemption.is_active
                            ? 'bg-green-900 text-green-300'
                            : 'bg-slate-700/50 text-slate-400'
                        }`}
                      >
                        {exemption.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex justify-end gap-2">
                        <button
                          className="p-2 text-orange-400 hover:bg-slate-700/50 rounded"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteExemption(exemption.id)}
                          className="p-2 text-red-400 hover:bg-slate-700/50 rounded"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Reports Tab */}
      {selectedTab === 'reports' && statistics && (
        <div className="space-y-6">
          {/* Tax by Country */}
          <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-6">
            <h3 className="text-lg font-bold text-white mb-4">Tax Collected by Country</h3>
            <div className="space-y-3">
              {statistics.by_country.slice(0, 10).map((country) => (
                <div key={country.country_code} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-white font-bold w-8">{country.country_code}</span>
                    <div className="flex-1">
                      <div className="h-2 bg-slate-700/50 rounded-full overflow-hidden w-64">
                        <div
                          className="h-full bg-blue-500"
                          style={{
                            width: `${
                              (country.tax_collected / statistics.total_tax_collected) * 100
                            }%`,
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-white font-bold">
                      {formatCurrency(country.tax_collected)}
                    </div>
                    <div className="text-slate-400 text-sm">{country.count} transactions</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Tax by Type */}
          <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-6">
            <h3 className="text-lg font-bold text-white mb-4">Tax Collected by Type</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {statistics.by_tax_type.map((type) => (
                <div key={type.tax_type} className="bg-slate-700/50 rounded-lg p-4">
                  <div className="text-slate-400 text-sm mb-1 uppercase">
                    {type.tax_type}
                  </div>
                  <div className="text-white text-2xl font-bold mb-1">
                    {formatCurrency(type.tax_collected)}
                  </div>
                  <div className="text-slate-400 text-sm">{type.count} transactions</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaxSettings;
